import {
    ByzzerChangeEventHandler,
    ByzzerSelect,
    ByzzerSelectOption,
} from '@byzzer/ui-components';
import classnames from 'classnames';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { SelectorLabelInfo } from '../SelectorLabelInfo';
import { ByzOshCharacteristics, CharacteristicDimension } from '@/types/ReportRun';

const baseClassName = 'byz-osh-characteristics-dimention-select';

const RAW_CHARACTERISTICS: ByzzerSelectOption<any, ByzOshCharacteristics>[] = [
    { display: 'BRAND OWNER HIGH', value: 'BRAND OWNER HIGH' },
    { display: 'BRANDED VS PRIVATE LABEL', value: 'BRANDED VS PRIVATE LABEL' },
    { display: 'BRAND HIGH', value: 'BRAND HIGH' },
];

export type ByzOshCharacteristicsDimensionSelectProps = {
    name?: string;
    value?: CharacteristicDimension[];
    categories?: string[];
    onChange?: ByzzerChangeEventHandler<CharacteristicDimension[]>;
    maxSelections?: number;
    includeCustomCharacteristics?: boolean;
    allowClear?: boolean;
    sku?: string;
    productLevel?: string;
    label?: ReactNode;
    required?: boolean;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>;

export function ByzzerOshCharacteristicsDimensionSelect({
    className,
    onChange,
    value = [],
    name,
    maxSelections,
    includeCustomCharacteristics = true,
    allowClear = true,
    label,
    sku = '',
    productLevel = 'Category',
    required,
    ...props
}: ByzOshCharacteristicsDimensionSelectProps) {
    const [selectedCharacteristics, setSelectedCharacteristics] = useState<CharacteristicDimension[]>([]);

    const characteristicsOptions = useMemo<CharacteristicDimension[]>(
        () =>
            RAW_CHARACTERISTICS.map(({ display, value }) => ({
                characteristicsCode: value,
                characteristic: display,
            })),
        []
    );

    const charDimLabel = useMemo(
        () => (
            <>
                <p>
                    <b>This report includes fields for characteristics that you get to define.</b>
                </p>
                <span>
                    Choose {maxSelections} characteristic(s) to show in your report. Category, Subcategory, and Brand
                    will always be included by default.
                </span>
                <span>
                    The dimension you choose will break the category down into comparative groups based on that
                    characteristic. Do you want to compare different brands, or the performance of different features
                    such as flavor or pack type?
                </span>
            </>
        ),
        [maxSelections]
    );

    useEffect(() => {
        setSelectedCharacteristics(value);
    }, [value]);

    function handleChange(e: ByzzerChangeEvent<string>) {        
        const updated = characteristicsOptions.filter((char) => e.value?.includes(char.characteristicsCode));
        setSelectedCharacteristics(updated);
        onChange?.({ name, value: updated });
    };

    return (
        <div className={classnames(baseClassName, className)} {...props}>
            {Number(sku) ? (
                <SelectorLabelInfo
                    sku={sku}
                    selectorCode="oshProductDimension"
                    required={required}
                    includeSuffix={!required}
                    max={maxSelections}
                    isLabelBold
                />
            ) : (
                charDimLabel
            )}

            <ByzzerSelect
                name={name}
                value={selectedCharacteristics.map((c) => c.characteristicsCode)}
                onChange={handleChange}
                label={label}
                options={characteristicsOptions.map((c) => ({
                    display: c.characteristic,
                    value: c.characteristicsCode,
                }))}
                placeholder="Select from the list"
                allowClear={allowClear}
                maxSelections={maxSelections || undefined}
                allowMultiple={Boolean(maxSelections)}
            />
        </div>
    );
}

export default ByzzerOshCharacteristicsDimensionSelect;
